// correlationId.js — id di correlazione cross-provider [sc-47909].
//
// Un unico id anonimo che accomuna, per lo stesso browser:
//   - i ticket di assistenza (central, via relay API)
//   - gli eventi GlitchTip/Sentry (tag lt_correlation_id)
//   - le sessioni/eventi Umami (identify)
// Serve a correlare "l'utente ha segnalato X" con "in quel momento è esploso Y".
//
// REGOLE PRIVACY (vincolanti):
// - L'id è RANDOM e ANONIMO: non deriva MAI da user id, email, nome o token.
// - Non è un identificatore di autenticazione: non dà accesso a nulla.
// - Il nome della chiave è volutamente `lt_correlation_id`: il sanitizer del relay
//   filtra qualunque chiave che contenga session/token/secret/credential/email/...,
//   quindi nomi tipo `sessionId` o `correlationToken` verrebbero sostituiti con
//   "[Filtered]" e la correlazione si perderebbe silenziosamente.
//
// Nessuna funzione qui deve MAI lanciare: se lo storage non è disponibile
// (Safari private mode, storage disabilitato) si degrada a un id volatile in memoria.

export const CORRELATION_ID_STORAGE_KEY = "lt_correlation_id";

// Fallback in memoria: usato quando localStorage non è leggibile/scrivibile.
// Resta stabile per la durata del tab, così la correlazione funziona comunque.
let volatileId = null;

// Genera un id random. Preferisce crypto.randomUUID, poi getRandomValues.
// L'ultimo fallback (timestamp+Math.random) NON è crittograficamente sicuro:
// va bene perché serve unicità pratica, non imprevedibilità.
function generateId() {
  try {
    const c = typeof crypto !== "undefined" ? crypto : null;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    if (c && typeof c.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      // Formato UUID v4 (variante/versione impostate a mano).
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
      return [
        hex.slice(0, 4).join(""),
        hex.slice(4, 6).join(""),
        hex.slice(6, 8).join(""),
        hex.slice(8, 10).join(""),
        hex.slice(10, 16).join(""),
      ].join("-");
    }
  } catch {
    /* cade sul fallback sotto */
  }
  return `lt-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

// Accetta solo id dalla forma attesa: evita di riusare valori manomessi a mano
// nello storage (o residui di altre app) come identificatore.
function isPlausibleId(value) {
  return typeof value === "string" && value.length >= 8 && value.length <= 64;
}

// Id di correlazione persistente per questo browser. Lo crea al primo accesso.
// Persistente (localStorage, non session) per correlare più sessioni dello
// stesso utente: è il comportamento utile per l'assistenza.
export function getCorrelationId() {
  try {
    const storage = typeof window !== "undefined" ? window.localStorage : null;
    if (storage) {
      const existing = storage.getItem(CORRELATION_ID_STORAGE_KEY);
      if (isPlausibleId(existing)) return existing;
      const fresh = generateId();
      storage.setItem(CORRELATION_ID_STORAGE_KEY, fresh);
      return fresh;
    }
  } catch {
    /* storage non disponibile: si prosegue con l'id volatile */
  }
  if (!volatileId) volatileId = generateId();
  return volatileId;
}

// Rigenera l'id (es. da usare al logout, se si vuole evitare che lo stesso id
// segua più operatori sulla stessa postazione). Non chiamata da nessuna parte
// per ora: esposta perché la decisione è di prodotto, non tecnica.
export function resetCorrelationId() {
  volatileId = null;
  try {
    const storage = typeof window !== "undefined" ? window.localStorage : null;
    if (storage) storage.removeItem(CORRELATION_ID_STORAGE_KEY);
  } catch {
    /* no-op */
  }
  return getCorrelationId();
}

export default {
  CORRELATION_ID_STORAGE_KEY,
  getCorrelationId,
  resetCorrelationId,
};
